import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { isAuthHydrated } from '../utils/authHydration'
import { getAuthToken } from '../utils/authStorage'
import { hasPendingPlusCheckout } from '../utils/pendingPlusCheckout'
import LoadingSpinner from './LoadingSpinner'
import { WorkspaceShellSkeleton } from './Skeleton'

const Home = lazy(() => import('../pages/Home'))

function workspacePathForRole(role: string): string {
  switch (role) {
    case 'admin':
      return '/admin/dashboard'
    case 'tutor':
      return '/tutor/profile'
    case 'school':
      return '/school/dashboard'
    case 'affiliate':
      return '/affiliate'
    default:
      return '/messages'
  }
}

/**
 * `/` entry: logged-in users go to their workspace (not the marketing homepage).
 * Eager (not behind the Home chunk) so auth redirect is not delayed by lazy load.
 */
export default function RootEntry() {
  const { isLoggedIn, user, _hasHydrated } = useAuthStore()
  const location = useLocation()
  const [hydrationReady, setHydrationReady] = useState(isAuthHydrated)

  useEffect(() => {
    if (isAuthHydrated()) {
      setHydrationReady(true)
      return
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      useAuthStore.getState().setHasHydrated(true)
      setHydrationReady(true)
    })
    const timeout = window.setTimeout(() => {
      useAuthStore.getState().setHasHydrated(true)
      setHydrationReady(true)
    }, 1200)
    return () => {
      unsub()
      window.clearTimeout(timeout)
    }
  }, [_hasHydrated])

  const hasToken = Boolean(getAuthToken())
  const viewingPlans = location.hash === '#plans'
  const likelySession =
    hasToken ||
    (typeof window !== 'undefined' && Boolean(window.localStorage.getItem('userRole')))

  if (!hydrationReady) {
    return likelySession ? <WorkspaceShellSkeleton /> : <LoadingSpinner />
  }

  /*
   * Signed-in on bare `/` → workspace.
   * Marketing homepage while signed in: use `/home` (and `/home#plans`).
   * Also stay on `/` when:
   * - /#plans (legacy plan deep-link)
   * - pending Plus/Pro checkout just after auth (HomeAuthPanel → Stripe)
   */
  if (
    isLoggedIn &&
    user &&
    hasToken &&
    !viewingPlans &&
    !hasPendingPlusCheckout()
  ) {
    return <Navigate to={workspacePathForRole(user.role)} replace />
  }

  return (
    <Suspense fallback={likelySession ? <WorkspaceShellSkeleton /> : <LoadingSpinner />}>
      <Home />
    </Suspense>
  )
}
